import {
    FacetGroup,
    FacetState,
    FacetValuesSortOrder,
    FieldType,
    HibuFacet,
    HibuFacetGroup,
    ReduxHibuFacetValue,
    ReduxSelectedFacetValueByFieldName,
    SelectionType
} from './facetTypes';
import {DeepReadonly} from 'utility-types';
import {ReduxNormalizedType} from '../app-types';

export function sortFacets(state: FacetState, sortField: FacetValuesSortOrder): void {
    for (const fieldName in state.facetGroups.byKey) {
        const facetGroup = state.facetGroups.byKey[fieldName] as FacetGroup;
        if (facetGroup.selectionType === SelectionType.RANGE) {
            continue;
        }
        const actualSortOrder = facetGroup.fieldType === FieldType.DIRPATH ? FacetValuesSortOrder.VALUE : sortField;
        const hibuFacets = getNormalizedHibuFacets(state, fieldName);
        // TODO: consider re-implementing using something like https://www.collectionsjs.com/sorted-set if performance is critical
        const hibuFacetValues = Object.values(hibuFacets.byKey) as HibuFacet[];
        facetGroup.hibuFacetValues = hibuFacetValues
            .sort(FacetSortFunctions[actualSortOrder])
            .map((facet) => facet.value);
    }
    state.facetValuesSortOrder = sortField;
}

const FacetSortFunctions = {
    [FacetValuesSortOrder.COUNT]: (a: HibuFacet, b: HibuFacet) => {
        return b.count - a.count;
    },
    [FacetValuesSortOrder.NAME]: (a: HibuFacet, b: HibuFacet) => {
        return a.name.localeCompare(b.name);
    },

    [FacetValuesSortOrder.VALUE]: (a: HibuFacet, b: HibuFacet) => {
        return a.value.localeCompare(b.value);
    },
};

/** INITIAL STATES */

export function initialSearchFacetState(): FacetState {
    return {
        hibuFacetValues: {
            byKey: {},
            allKeys: []

        },
        facetGroups: {
            byKey: {},
            allKeys: []
        },
        expandedFacetGroups: {},
        selectedFacetValuesByFieldName: {
            byKey: {},
            allKeys: []
        },
        facetValuesSortOrder: FacetValuesSortOrder.COUNT,
    };
}

/** STATE UTILITIES */
export function isClearable(state: DeepReadonly<FacetState>, fieldName: string): boolean {
    return state.facetGroups.byKey[fieldName]?.clearable;
}

/**
 * Returns {@link HibuFacet}s of the group with {@code fieldName} in an object (map), keyed by the respective value.
 * @param state to be used
 * @param fieldName of the group
 */
export function getNormalizedHibuFacets(
    state: DeepReadonly<FacetState>,
    fieldName: string
): ReduxNormalizedType<HibuFacet> {
    const normalizedValues: ReduxNormalizedType<HibuFacet> = {allKeys: [], byKey: {}};
    const facetGroup = state.facetGroups.byKey[fieldName];
    if (facetGroup) {
        facetGroup.hibuFacetValues.forEach((value) => {
            const stateHibuFacetValues = state.hibuFacetValues.byKey[facetGroup.fieldName] as ReduxHibuFacetValue;
            normalizedValues.byKey[value] = stateHibuFacetValues[value];
            normalizedValues.allKeys.push(value);
        });
        return normalizedValues;
    } else {
        return normalizedValues;
    }
}

function initFacetGroupData(facetState: FacetState, facetGroup: FacetGroup) {
    facetGroup.hibuFacetValues = [];
    facetState.hibuFacetValues.byKey[facetGroup.fieldName] = {};
    facetState.hibuFacetValues.allKeys = [];
}

function addHibuFacetGroupDataToState(facetState: FacetState, hibuFacetGroup: HibuFacetGroup) {
    const facetGroup = getFacetGroupByFieldName(facetState, hibuFacetGroup.fieldName);
    const stateHibuFacetValues = facetState.hibuFacetValues.byKey[hibuFacetGroup.fieldName] as ReduxHibuFacetValue;
    const allPaths = hibuFacetGroup.values.map((value) => value.value);

    facetGroup.errorCode = hibuFacetGroup.errorCode;

    hibuFacetGroup.values.forEach((hibuFacet: HibuFacet) => {
        if (facetGroup.fieldType === FieldType.DIRPATH) {
            hibuFacet.name = dirPathSortName(hibuFacet.value, allPaths);
        }
        if (!facetGroup.hibuFacetValues.includes(hibuFacet.value)) {
            facetGroup.hibuFacetValues.push(hibuFacet.value);
        }
        stateHibuFacetValues[hibuFacet.value] = hibuFacet;
        if (!facetState.hibuFacetValues.allKeys.includes(hibuFacet.value)) {
            facetState.hibuFacetValues.allKeys.push(hibuFacet.value);
        }
    });
}

export function dirPathSortName(path: string, othersPaths: string[]): string {
    if (path.indexOf('/') === -1 || path === '/') {
        return path;
    }

    const splitedPath: string[] = path.split('/');
    const countDir = splitedPath.length - 1;
    let countSousDirWithSamePath = countDir;
    const lastDirOnPath = splitedPath[countDir];
    let pathsWithSameEnd = othersPaths.filter((otherPath) => otherPath.endsWith(lastDirOnPath));

    //see where the path start, e.g. path : /something/something, splitedPath is
    // ["", "something", "something"] -> lastValidPathElement is 1
    let lastValidPathElement = 0;
    while (splitedPath[lastValidPathElement] === '') {
        lastValidPathElement++;
    }

    while (pathsWithSameEnd.length > 0 && countSousDirWithSamePath > lastValidPathElement) {
        const commonPath = splitedPath.slice(0, countSousDirWithSamePath + 1).join('/');
        pathsWithSameEnd = pathsWithSameEnd.filter((otherPath) => otherPath.startsWith(commonPath));
        if (pathsWithSameEnd.length > 0) {
            countSousDirWithSamePath--;
        }
    }
    const separator = document.body?.clientWidth <= 1024 ? ' ' : '   ';
    const subfolderCharacter = `<div class="subfolder-character">${separator.repeat(Math.max(countDir - countSousDirWithSamePath - 1, 0))}└ </div>`;
    return `<div class="subfolder-facet-value">${subfolderCharacter}<div class="subfolder-facet-text">${lastDirOnPath}</div></div>`;
}

function getFacetGroupByFieldName(facetState: FacetState, fieldName: string): FacetGroup {
    if (!facetState.facetGroups.byKey[fieldName]) {
        facetState.facetGroups.byKey[fieldName] = initUnknownFacetGroup(fieldName);
        facetState.facetGroups.allKeys.push(fieldName);
    }
    return facetState.facetGroups.byKey[fieldName] as FacetGroup;
}

export function initUnknownFacetGroup(fieldName: string): FacetGroup {
    if (fieldName.endsWith('_date')) {
        return new FacetGroup(fieldName, FieldType.DATE, [], undefined, SelectionType.RANGE);
    } else if (fieldName.endsWith('_dirPath')) {
        return new FacetGroup(fieldName, FieldType.DIRPATH);
    } else {
        return new FacetGroup(fieldName, FieldType.STRING);
    }
}

export function replaceFacetsFromSearch(facetState: FacetState, responseFacets: HibuFacetGroup[]): void {
    if (!responseFacets) {
        return;
    }

    responseFacets.forEach((facetGroupFromResponse: HibuFacetGroup) => {
        const facetGroup = getFacetGroupByFieldName(facetState, facetGroupFromResponse.fieldName);
        if (facetGroup) {
            initFacetGroupData(facetState, facetGroup);
            addHibuFacetGroupDataToState(facetState, facetGroupFromResponse);
        }
        // TODO: as soon as hibu issue IN-163 is implemented, don't sort the facets here anymore
        if (facetState.facetValuesSortOrder === FacetValuesSortOrder.NAME) {
            sortFacets(facetState, facetState.facetValuesSortOrder);
        }
    });
}

export function updateSelectedFacets(
    facetState: FacetState,
    facetValues: ReduxSelectedFacetValueByFieldName,
    containerFacets: string[]
): void {
    Object.entries(facetValues).forEach((entry) => {
        updateSelectedFacetsByField(facetState, entry[0], entry[1], containerFacets);
    });
}

export function updateSelectedFacetsByField(
    facetState: FacetState,
    fieldName: string,
    newSelectedFacetValues: string[],
    containerFacets: string[]
): void {
    const noSelectedFacets = newSelectedFacetValues.length === 0;

    const selectedFacets = facetState.selectedFacetValuesByFieldName;

    // handle allIds
    if (fieldName in selectedFacets.byKey) {
        if (noSelectedFacets) {
            // facetGroup had selected facets before, but not anymore
            selectedFacets.allKeys = selectedFacets.allKeys.filter((name) => name !== fieldName);
        }
    } else {
        // facetGroup didn't have selected facets before and now does
        selectedFacets.allKeys.push(fieldName);
    }

    // handle byKey
    if (noSelectedFacets) {
        delete selectedFacets.byKey[fieldName];
        if (!containerFacets.includes(fieldName)) {
            delete facetState.facetGroups.byKey[fieldName];
        }
    } else {
        selectedFacets.byKey[fieldName] = newSelectedFacetValues;
    }
}

export function expandCollapseFacetGroup(facetState: FacetState, fieldName: string): void {
    const previousExpanded = facetState.expandedFacetGroups[fieldName];
    facetState.expandedFacetGroups[fieldName] = !previousExpanded;
}

export function expandFacetGroup(facetState: FacetState, fieldName: string): void {
    facetState.expandedFacetGroups[fieldName] = true;
}
